// Deterministic color slot + display initials for generated initials avatars
// (the fallback for users without an uploaded avatar).
//
// The slot number selects a --color-avatar-<n>-bg/text CSS variable pair, so
// the palette lives in _variables.scss and switches with the active theme.
// AVATAR_COLOR_SLOTS and the hash below must stay in sync with the SCSS tokens
// and with the server-side generator (app/lib/initials_avatar_svg.rb), which
// renders the same avatar for legacy consumers of /api/v1/users/:id/avatar.
export const AVATAR_COLOR_SLOTS = 7

const graphemeSegmenter =
    typeof Intl !== 'undefined' && 'Segmenter' in Intl
        ? new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' })
        : null

export function avatarGraphemes(value: string): string[] {
    if (!graphemeSegmenter) return Array.from(value)
    return Array.from(graphemeSegmenter.segment(value), (part: any) => part.segment)
}

export function avatarColorSlot(seed: string | number | null | undefined): number {
    const s = String(seed ?? '')
    let h = 0
    for (const ch of s) {
        h = (h * 31 + (ch.codePointAt(0) || 0)) % 4294967296
    }
    return (h % AVATAR_COLOR_SLOTS) + 1
}

// Up to two uppercased characters: word initials for multi-word names
// ("John Smith" → JS), first two graphemes for single-word handles
// ("mitya" → MI) — two letters keep similar handles distinct (mitya vs
// metamitya). Names whose first grapheme isn't a letter or number (e.g. an
// emoji) keep just that grapheme.
export function avatarInitials(name: string | null | undefined): string | null {
    const trimmed = (name || '').trim()
    if (!trimmed) return null
    const words = trimmed.split(/\s+/)
    const first = avatarGraphemes(words[0])
    if (!first[0]) return null
    if (!/[\p{L}\p{N}]/u.test(first[0])) return first[0]
    const second = words.length > 1 ? avatarGraphemes(words[1])[0] : first[1]
    const pair = second && /[\p{L}\p{N}]/u.test(second) ? first[0] + second : first[0]
    return pair.toUpperCase()
}

// Prefer the user id so the color survives renames; fall back to name for
// payloads that carry no id.
export function avatarColorSeed(user: {
    id?: string | number | null
    name?: string | null
}): string {
    if (user?.id !== null && user?.id !== undefined && user?.id !== '') return String(user.id)
    return user?.name || ''
}
